'use client'

import React from 'react'
import { Product } from '@/payload-types'
import { ProductGridItem } from '@/components/ProductGridItem'
import { SectionHeader } from '@/components/ui/SectionHeader'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import Autoplay from 'embla-carousel-autoplay'
import { useTranslations } from 'next-intl'

interface FeaturedProductsProps {
  products: Product[]
  title?: string
}

export const FeaturedProducts = ({ products, title }: FeaturedProductsProps) => {
  const t = useTranslations('product')
  const commonT = useTranslations('common')
  if (!products || products.length === 0) return null

  return (
    <div className="w-full max-w-[80rem] mx-auto my-3">
      <SectionHeader
        title={title || t('bestSellers')}
        href="/shop"
        buttonLabel={commonT('viewAll')}
        testId="featured-title"
      />
      <section
        data-testid="featured-products"
        className="w-full px-3 sm:px-5 lg:px-6 py-3 sm:py-4 lg:py-6 bg-white/5 dark:bg-black/20 backdrop-blur-2xl border border-white/20 rounded-2xl shadow-2xl transition-all duration-300 hover:bg-white/10 dark:hover:bg-black/30"
        style={{
          boxShadow:
            'inset 0 1px 0 rgba(255,255,255,0.15), inset 0 -40px 80px rgba(0,0,0,0.25), 0 8px 32px rgba(0,0,0,0.3)',
        }}
      >
        {/* Carousel wrapper for button positioning at section edges */}
        <Carousel
          opts={{
            align: 'start',
            loop: true,
          }}
          plugins={[
            Autoplay({
              delay: 4000,
              stopOnInteraction: true,
            }),
          ]}
          className="relative w-full"
        >
          {/* Inner content with gutters for products */}
          <div className="px-4 sm:px-6 lg:px-8">
            <CarouselContent data-testid="products-grid" className="-ml-4">
              {products.map((product) => (
                <CarouselItem
                  key={product.id}
                  data-testid="product-item"
                  className="pl-4 md:basis-1/2 lg:basis-1/4"
                >
                  <div className="h-full">
                    <ProductGridItem product={product} />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </div>

          {/* Dark glass pill arrows — vertically centered on product images */}
          <CarouselPrevious
            variant="neon-gray"
            className="absolute left-0 top-1/2 -translate-y-1/2 
              w-5 h-20 sm:h-24 lg:h-28 
              rounded-l-lg rounded-r-none z-30 !py-0 
              flex items-center justify-center
              bg-black/30 hover:bg-black/50 dark:bg-black/50 dark:hover:bg-black/70
              border-0 border-r border-r-white/10
              shadow-[2px_0_12px_rgba(0,0,0,0.2)]
              dark:hover:!shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]
              backdrop-blur-xl
              transition-all duration-300
              hover:!-translate-y-1/2
              text-white/50 hover:text-white
              [&>svg]:w-4 [&>svg]:h-4"
          />
          <CarouselNext
            variant="neon-gray"
            className="absolute right-0 top-1/2 -translate-y-1/2 
              w-5 h-20 sm:h-24 lg:h-28 
              rounded-r-lg rounded-l-none z-30 !py-0 
              flex items-center justify-center
              bg-black/30 hover:bg-black/50 dark:bg-black/50 dark:hover:bg-black/70
              border-0 border-l border-l-white/10
              shadow-[-2px_0_12px_rgba(0,0,0,0.2)]
              dark:hover:!shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]
              backdrop-blur-xl
              transition-all duration-300
              hover:!-translate-y-1/2
              text-white/50 hover:text-white
              [&>svg]:w-4 [&>svg]:h-4"
          />
        </Carousel>
      </section>
    </div>
  )
}

